<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const isLoading = ref(false);
const rows = ref([]);

const fetchRates = async () => {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.getResponseRates(props.contactId);
    rows.value = (data?.inboxes || []).filter(r => r.sample_size > 0);
  } catch {
    rows.value = [];
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchRates);
watch(() => props.contactId, fetchRates);

const hasData = computed(() => rows.value.length > 0);

const percent = rate => `${Math.round((rate || 0) * 100)}%`;
</script>

<template>
  <div v-if="hasData" class="flex flex-col w-full gap-3">
    <div class="flex flex-col gap-0.5">
      <h6 class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.RESPONSE_RATES.TITLE') }}
      </h6>
      <span class="text-xs text-n-slate-10">
        {{ t('CONTACTS_LAYOUT.DETAILS.RESPONSE_RATES.SUBTITLE') }}
      </span>
    </div>
    <div class="flex flex-col w-full gap-2">
      <div
        v-for="row in rows"
        :key="row.inbox_id"
        class="flex flex-col w-full gap-1"
      >
        <div class="flex flex-row items-center justify-between gap-2 text-sm">
          <span class="text-n-slate-11 truncate">{{ row.name }}</span>
          <span class="text-n-slate-12 font-medium tabular-nums">
            {{ percent(row.response_rate) }}
          </span>
        </div>
        <div class="h-1.5 w-full rounded-full bg-n-alpha-black2 overflow-hidden">
          <div
            class="h-full bg-n-brand"
            :style="{ width: percent(row.response_rate) }"
          />
        </div>
        <span class="text-xs text-n-slate-10">
          {{
            t('CONTACTS_LAYOUT.DETAILS.RESPONSE_RATES.SAMPLE', {
              n: row.sample_size,
            })
          }}
        </span>
      </div>
    </div>
  </div>
</template>
